import { useId, useState } from 'react';

/**
 * A small (i) that explains the thing next to it.
 *
 * This exists for the pieces *inside* a chart — a KPI tile, a hero stat — that
 * each need their own explanation. The chart card's own (i) (`ChartFrame`) is
 * deliberately left alone: it is part of the card chrome, revealed on card
 * hover and positioned against the card's corner, so folding the two into one
 * component would mean a prop for every one of those differences. What they do
 * share is the behaviour, and that is what is reproduced here exactly: hover or
 * focus reveals, a click pins it open for touch, where there is no hover.
 */

interface Props {
  /** What the tooltip says. Nothing renders when this is blank. */
  text?: string;
  /** Names the thing being explained, for the button's accessible label. */
  about: string;
}

export const InfoTip = ({ text, about }: Props) => {
  const id = useId();
  const [pinned, setPinned] = useState(false);

  if (!text?.trim()) return null;

  return (
    <span
      className={`tip-wrap${pinned ? ' is-pinned' : ''}`}
      onMouseLeave={() => setPinned(false)}
    >
      <button
        type="button"
        className="tip-btn"
        aria-describedby={id}
        aria-expanded={pinned}
        aria-label={`About ${about}`}
        onClick={() => setPinned((open) => !open)}
      >
        i
      </button>
      <span role="tooltip" id={id} className="tip-bubble">
        {text}
      </span>
    </span>
  );
};
